import { Button, Flex, Modal, Select } from 'antd';
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { useGetOrganizationUsersQuery } from '@app/api/Users';
import { CloseIconsForModalWithDataTestId } from '@app/components';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { adaptDataToSelect } from '@app/utils';

import type { GetSecretParams } from '@pages/Organizations/OrganizationsList/types';

import type { BaseOptionType } from 'antd/es/select';

const systemId = 1;

export type SelectOrganizationUserModalApi = {
  open: ({
    databaseId,
    organizationId,
  }: {
    databaseId: string;
    organizationId: number;
  }) => void;
  clear: () => void;
};

type SelectOrganizationUserModalProps = {
  closeCb: (data: GetSecretParams) => void;
};

export const SelectOrganizationUserModal = forwardRef<
  SelectOrganizationUserModalApi,
  SelectOrganizationUserModalProps
>(({ closeCb }, ref) => {
  const [visible, toggleVisibility] = useState(false);
  const [databaseId, setDatabaseId] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<number>();
  const { data, error } = useGetOrganizationUsersQuery(
    { databaseId, ActiveBranchId: 1 },
    { skip: !databaseId }
  );

  const { showNotification } = useContext(NotificationContext);

  const options: BaseOptionType[] = useMemo(() => {
    return data ? adaptDataToSelect(data) : [];
  }, [data]);

  useImperativeHandle(ref, () => ({
    open: ({ databaseId }) => {
      toggleVisibility(true);
      setDatabaseId(databaseId);
    },
    clear: () => setSelectedUserId(undefined),
  }));

  const chooseHandler = () => {
    closeCb &&
      selectedUserId &&
      closeCb({
        databaseId,
        forUserId: selectedUserId,
      });
    toggleVisibility(false);
  };

  const closeHandler = () => {
    toggleVisibility(false);
  };

  const changeHandler = (value: number) => {
    setSelectedUserId(value);
  };

  useEffect(() => {
    if (error) {
      showNotification(handleCatchError(error));
    }
  }, [error, showNotification]);

  return (
    <Modal
      centered
      open={visible}
      onCancel={closeHandler}
      title={generateSpanWithDataTestId(
        'Выбор пользователя организации',
        'choose-user-organization-title'
      )}
      data-testid="сhoose-user-organization-popup"
      closeIcon={<CloseIconsForModalWithDataTestId />}
      footer={
        <Flex gap={10} justify="end">
          <Button type="default" onClick={closeHandler} data-testid="cancel">
            Отмена
          </Button>
          <Button type="primary" onClick={chooseHandler} data-testid="select">
            Выбрать
          </Button>
        </Flex>
      }
    >
      <Select
        value={selectedUserId}
        placeholder="Выберите пользователя"
        size="large"
        options={options.filter(({ value }) => value !== systemId)}
        style={{ width: '100%', marginTop: '2rem', marginBottom: '2rem' }}
        onChange={changeHandler}
        showSearch
        filterOption={(input, option) =>
          option?.label.toLowerCase().includes(input.toLowerCase())
        }
        className="user-select"
        popupRender={(menu) => <div data-testid="user-dropdown">{menu}</div>}
        optionRender={(option) => (
          <span data-testid="user-item">{option.label}</span>
        )}
      />
    </Modal>
  );
});
